import React from 'react';
import {findKey} from 'underscore';

import {assert} from 'Asserts';
import {abbreviateList} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {massUnitTypePredicate, molUnitTypePredicate} from 'inventory/InventoryUnitUtil';
import {DEFAULT_DEFAULT_VALUE} from 'notebook/data-tables/DataTableConstants';
import {
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
  ChemistryReactionRole,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import limitingReactantCustomEval from 'notebook/integrations/chemistry/custom-evals/LimitingReactantCustomEval';
import {getMolecularFormulaColumnCustomEval} from 'notebook/integrations/chemistry/custom-evals/MolecularFormulaCustomEval';
import {getMolecularWeightColumnCustomEval} from 'notebook/integrations/chemistry/custom-evals/MolecularWeightCustomEval';
import makeStoichiometryCalculationCustomEval, {
  OrderedStoichiometryCalculationVariables,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/makeStoichiometryCalculationCustomEval';
import {StoichiometryVariable} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryEquations';
import StoichiometrySolver, {
  STOICHIOMETRY_CALCULATION_AVAILABLE_VARIABLES,
  STOICHIOMETRY_CALCULATION_CIRCULAR_VARIABLES,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometrySolver';
import {getDefaultUnitCustomEval} from 'notebook/integrations/chemistry/custom-evals/StoichiometryUnitDefaultsCustomEval';
import StoichiometryTableEmbeddedChemicalSketcher from 'notebook/integrations/chemistry/StoichiometryTableEmbeddedChemicalSketcher';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableRow,
  StructuredTableColumn,
  StructuredTableCustomComponentSlotProps,
  StructuredTableColumnGroups,
  CustomStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {toSpreadsheetBooleanValue} from 'notebook/tables/excess/Util';
import {
  RowMetadata,
  TABLE_MINI_COLUMN_WIDTH,
  TABLE_SMALL_COLUMN_WIDTH,
  CellIndex,
  CustomEval,
  TableColumnType,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {BooleanCellDisplayMode} from 'schemas/tags/BooleanValueCellEditor';
import {hasKey} from 'util/Types';

export interface StoichiometryStructuredTableRowMetadata extends RowMetadata {}

export interface StoichiometryStructuredTableMetadata {
  reactionMrvExternalFile?: {id: string; url: string};
}

export type StoichiometryStructuredTable = StructuredTable<
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata
>;
export type StoichiometryTableCustomSlotProps = StructuredTableCustomComponentSlotProps<
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata
>;

type ColumnGroupToIndexMap = Partial<Record<StructuredTableColumnGroups, number>>;

const LIMITING_REACTANT_ROW_INDEX = 0;

const REACTION_ROLE_OPTIONS = Object.values(ChemistryReactionRole).map(
  (role) => CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]
);

/** There should only be 1 of each of these columns in a StoichiometryStructuredTable */
const STOICHIOMETRY_TABLE_COLUMN_GROUPS = new Set([
  StructuredTableColumnGroups.CHEMISTRY_MOLECULE,
  StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE,
  StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE,
  StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT,
  StructuredTableColumnGroups.CHEMISTRY_MOLS,
  StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT,
  StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN,
  StructuredTableColumnGroups.CHEMISTRY_AMOUNT_ACTUAL,
  StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT,
  StructuredTableColumnGroups.CHEMISTRY_CHEMICAL_FORMULA,
]);

const COLUMN_GROUP_TO_STOICHIOMETRY_VARIABLE_MAP = {
  [StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT]: StoichiometryVariable.MOLECULAR_WEIGHT,
  [StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE]: StoichiometryVariable.EQUIVALENCE,
  [StructuredTableColumnGroups.CHEMISTRY_MOLS]: StoichiometryVariable.MOLS,
  [StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT]: StoichiometryVariable.MOL_UNIT_CONVERSION,
  [StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN]: StoichiometryVariable.AMOUNT,
  [StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT]: StoichiometryVariable.AMOUNT_UNIT_CONVERSION,
} satisfies Partial<Record<StructuredTableColumnGroups, StoichiometryVariable>>;

const LIMITING_REACTANT_COLUMN_GROUP_TO_STOICHIOMETRY_VARIABLE_MAP = {
  [StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE]: StoichiometryVariable.LIMITING_REACTANT_EQUIVALENCE,
  [StructuredTableColumnGroups.CHEMISTRY_MOLS]: StoichiometryVariable.LIMITING_REACTANT_MOLS,
  [StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT]:
    StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION,
} satisfies Partial<Record<StructuredTableColumnGroups, StoichiometryVariable>>;

const COLUMN_GROUP_TO_NAME_MAP = {
  [StructuredTableColumnGroups.CHEMISTRY_MOLECULE]: 'Compound',
  [StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE]: 'Role',
  [StructuredTableColumnGroups.CHEMISTRY_LIMITING_REACTANT]: 'LR',
  [StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE]: 'Equiv',
  [StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT]: 'MW',
  [StructuredTableColumnGroups.CHEMISTRY_MOLS]: 'Mol (plan)',
  [StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT]: 'Mol Unit',
  [StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN]: 'Amt (plan)',
  [StructuredTableColumnGroups.CHEMISTRY_AMOUNT_ACTUAL]: 'Amt (act)',
  [StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT]: 'Amt Unit',
  [StructuredTableColumnGroups.CHEMISTRY_CHEMICAL_FORMULA]: 'Formula',
} satisfies Partial<Record<StructuredTableColumnGroups, string>>;

export const DEFAULT_MOL_UNIT_SYMBOL = 'mol';
export const DEFAULT_MASS_UNIT_SYMBOL = 'g';
const DEFAULT_MW_PRECISION = 3;
const DEFAULT_EQUIV_PRECISION = 2;
const DEFAULT_QUANTITY_PRECISION = 4;

export default class StoichiometryStructuredTableAdapter extends GenericStructuredTableAdapter<
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata
> {
  getBlankRowMetadata(
    tableMetadata: StoichiometryStructuredTableMetadata
  ): StoichiometryStructuredTableRowMetadata {
    return {};
  }

  override getDisplayName(structuredTable: StoichiometryStructuredTable): string {
    return 'Reaction';
  }

  override allowSingleInputs(_: StoichiometryStructuredTable): boolean {
    return false;
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: StoichiometryStructuredTableMetadata;
  }): StructuredTableColumn {
    switch (column.columnGroup) {
      case StructuredTableColumnGroups.CHEMISTRY_MOLECULE: {
        return {
          ...column,
          frontEndMetadata: {
            validSchemas: 'ALL',
          },
        };
      }
      case StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE: {
        assert(
          column.columnType === TableColumnType.OPTION_SELECTOR,
          'Reaction Role column must be an OPTION_SELECTOR'
        );
        return {
          ...column,
          options: REACTION_ROLE_OPTIONS,
        };
      }
      case StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT: {
        return {
          ...column,
          frontEndMetadata: {
            unitTypesFilterPredicate: molUnitTypePredicate,
          },
        };
      }
      case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT: {
        return {
          ...column,
          frontEndMetadata: {
            unitTypesFilterPredicate: massUnitTypePredicate,
          },
        };
      }
      default:
        return column;
    }
  }

  private makeStoichiometryStructuredTableColumn(
    columnOptions: Omit<Parameters<typeof makeStructuredTableColumn>[0], 'name'> & {name?: string}
  ): StructuredTableColumn {
    const {columnGroup} = columnOptions;
    return makeStructuredTableColumn({
      ...columnOptions,
      name:
        columnOptions.name ??
        ((columnGroup &&
          hasKey(COLUMN_GROUP_TO_NAME_MAP, columnGroup) &&
          COLUMN_GROUP_TO_NAME_MAP[columnGroup]) ||
          ''),
    });
  }

  /**
   * ⚠️ CHEMISTRY_TABLE: There is a need to create more general column types that can be used for purely
   * typed input. This may be Custom Columns, but it may be something else (schema-less schema fields)
   *
   * Helper to make a decimal column
   */
  private makeDecimalStoichiometryStructuredTableColumn(
    overrides: Partial<CustomStructuredTableColumn> = {}
  ): CustomStructuredTableColumn {
    const {columnGroup} = overrides;
    return {
      ...this.makeStoichiometryStructuredTableColumn({
        columnGroup,
        columnType: TableColumnType.CUSTOM,
      }),
      columnType: TableColumnType.CUSTOM,
      customColumnConfig: {dataType: CustomColumnDataType.FLOAT},
      ...overrides,
    };
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string | null;
    metadata: StoichiometryStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return [
      {
        ...this.makeStoichiometryStructuredTableColumn({
          columnType: TableColumnType.EXISTING_BIOENTITY,
          columnGroup: StructuredTableColumnGroups.CHEMISTRY_MOLECULE,
        }),
        columnType: TableColumnType.EXISTING_BIOENTITY,
        bioentityTypeFilters: [ModelTypeConstants.MOLECULE],
      },
      {
        ...this.makeStoichiometryStructuredTableColumn({
          columnType: TableColumnType.OPTION_SELECTOR,
          columnGroup: StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE,
        }),
        columnType: TableColumnType.OPTION_SELECTOR,
        options: REACTION_ROLE_OPTIONS,
      },
      {
        ...this.makeStoichiometryStructuredTableColumn({
          columnType: TableColumnType.CUSTOM,
          columnGroup: StructuredTableColumnGroups.CHEMISTRY_LIMITING_REACTANT,
          width: TABLE_MINI_COLUMN_WIDTH,
          isReadOnly: true,
        }),
        columnType: TableColumnType.CUSTOM,
        customColumnConfig: {
          dataType: CustomColumnDataType.BOOLEAN,
          displayMode: BooleanCellDisplayMode.CHECKBOX,
        },
      },
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE,
        width: TABLE_SMALL_COLUMN_WIDTH,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_EQUIV_PRECISION},
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_MW_PRECISION},
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_MOLS,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_QUANTITY_PRECISION},
      }),
      this.makeStoichiometryStructuredTableColumn({
        columnType: TableColumnType.UNIT_REFERENCE,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_QUANTITY_PRECISION},
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_QUANTITY_PRECISION},
      }),
      this.makeDecimalStoichiometryStructuredTableColumn({
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_AMOUNT_ACTUAL,
        defaultValue: {...DEFAULT_DEFAULT_VALUE, precision: DEFAULT_QUANTITY_PRECISION},
      }),
      this.makeStoichiometryStructuredTableColumn({
        columnType: TableColumnType.UNIT_REFERENCE,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT,
      }),
      this.makeStoichiometryStructuredTableColumn({
        columnType: TableColumnType.STRING,
        columnGroup: StructuredTableColumnGroups.CHEMISTRY_CHEMICAL_FORMULA,
        isReadOnly: true,
      }),
    ];
  }

  private getStoichiometryTableColumnGroupToIndexMap(
    columns: Array<StructuredTableColumn>
  ): ColumnGroupToIndexMap {
    return columns.reduce<ColumnGroupToIndexMap>((acc, column, index) => {
      const {columnGroup} = column;
      if (columnGroup && STOICHIOMETRY_TABLE_COLUMN_GROUPS.has(columnGroup as StructuredTableColumnGroups)) {
        const structuredTableColumnGroup = columnGroup as StructuredTableColumnGroups;
        assert(
          acc[structuredTableColumnGroup] == null,
          `There should only be at most 1 column for each Stoichiometry Table Column Group.`,
          {structuredTableColumnGroup}
        );
        acc[structuredTableColumnGroup] = index;
      }
      return acc;
    }, {});
  }

  private getStoichiometryVariableFromColumnGroup(
    columnGroup:
      | StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE
      | StructuredTableColumnGroups.CHEMISTRY_MOLS
      | StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN
  ): StoichiometryVariable {
    return COLUMN_GROUP_TO_STOICHIOMETRY_VARIABLE_MAP[columnGroup];
  }

  private getStoichiometryVariableToCellIndexMap(
    structuredTable: StoichiometryStructuredTable,
    rowIndex: number,
    limitingReactantRowIndex: number
  ): Partial<Record<StoichiometryVariable, CellIndex>> {
    const isLimitingReactant = limitingReactantRowIndex === rowIndex;
    const variableToCellIndexMap: Partial<Record<StoichiometryVariable, CellIndex>> = {};
    for (const [columnIndex, column] of structuredTable.columns.entries()) {
      const {columnGroup} = column;
      if (!columnGroup || !hasKey(COLUMN_GROUP_TO_STOICHIOMETRY_VARIABLE_MAP, columnGroup)) {
        continue;
      }

      const variable = COLUMN_GROUP_TO_STOICHIOMETRY_VARIABLE_MAP[columnGroup];
      variableToCellIndexMap[variable] = {row: rowIndex, column: columnIndex};
      // If this is not the limiting reactant, then also add the limiting reactant cells
      if (
        !isLimitingReactant &&
        hasKey(LIMITING_REACTANT_COLUMN_GROUP_TO_STOICHIOMETRY_VARIABLE_MAP, columnGroup)
      ) {
        const limitingReactantVariable =
          LIMITING_REACTANT_COLUMN_GROUP_TO_STOICHIOMETRY_VARIABLE_MAP[columnGroup];
        variableToCellIndexMap[limitingReactantVariable] = {
          row: limitingReactantRowIndex,
          column: columnIndex,
        };
      }
    }
    return variableToCellIndexMap;
  }

  private getCustomEvalDependencies(
    structuredTable: StoichiometryStructuredTable,
    currentCellIndex: CellIndex
  ): Array<CellIndex | string> {
    const {column: columnIndex, row: rowIndex} = currentCellIndex;
    const column = structuredTable.columns[columnIndex];
    const {columnGroup} = column;
    if (!columnGroup) {
      return [];
    }
    const columnGroupToIndexMap = this.getStoichiometryTableColumnGroupToIndexMap(structuredTable.columns);
    switch (columnGroup) {
      case StructuredTableColumnGroups.CHEMISTRY_CHEMICAL_FORMULA:
      case StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT: {
        const moleculeColumnIndex = columnGroupToIndexMap[StructuredTableColumnGroups.CHEMISTRY_MOLECULE];
        return moleculeColumnIndex != null ? [{row: rowIndex, column: moleculeColumnIndex}] : [];
      }
      case StructuredTableColumnGroups.CHEMISTRY_LIMITING_REACTANT: {
        const reactionRoleColumnIndex =
          columnGroupToIndexMap[StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE];
        return [
          toSpreadsheetBooleanValue(rowIndex === LIMITING_REACTANT_ROW_INDEX),
          reactionRoleColumnIndex ? {row: rowIndex, column: reactionRoleColumnIndex} : '',
        ];
      }
      // These 3 columns can all solve for each other, but we cannot make circular dependencies for the
      // cell references. To combat this, we check to see if any of the variables are manually filled by the
      // user (either directly or via a formula). If so, that variable becomes the "anchor" variable and
      // calculations will be based on that variable (and other columns will become locked).
      case StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE:
      case StructuredTableColumnGroups.CHEMISTRY_MOLS:
      case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN: {
        return this.getStoichiometryCalculationCustomEvalDependencies(
          structuredTable,
          currentCellIndex,
          columnGroup
        );
      }
      default:
        return [];
    }
  }

  private getStoichiometryCalculationCustomEvalDependencies(
    structuredTable: StoichiometryStructuredTable,
    currentCellIndex: CellIndex,
    columnGroup:
      | StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE
      | StructuredTableColumnGroups.CHEMISTRY_MOLS
      | StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN
  ): Array<CellIndex | string> {
    const {row: rowIndex} = currentCellIndex;
    const solveForVariable = COLUMN_GROUP_TO_STOICHIOMETRY_VARIABLE_MAP[columnGroup];
    const limitingReactantRowIndex = LIMITING_REACTANT_ROW_INDEX;
    const isLimitingReactant = limitingReactantRowIndex === rowIndex;
    const variableToCellIndexMap = this.getStoichiometryVariableToCellIndexMap(
      structuredTable,
      rowIndex,
      limitingReactantRowIndex
    );
    // The limiting reactant equivalence will always be 1, so no cell dependencies are needed.
    if (isLimitingReactant && solveForVariable === StoichiometryVariable.EQUIVALENCE) {
      return ['', toSpreadsheetBooleanValue(true)];
    }

    const circularVariables = new Set(STOICHIOMETRY_CALCULATION_CIRCULAR_VARIABLES);
    // Don't consider the limiting reactant equivalence as a circular variable since it is always 1.
    if (isLimitingReactant) {
      circularVariables.delete(StoichiometryVariable.EQUIVALENCE);
    }
    // Check which of the circularly-dependent variables have been manually specified by the user.
    const filledCircularVariables = [...circularVariables].filter((variable) => {
      const cellIdx = variableToCellIndexMap[variable];
      return Boolean(cellIdx && !TableUtil.isCellEmpty(structuredTable.rows[rowIndex].cells[cellIdx.column]));
    });
    // If exactly 1 of the circularly-dependent variables has been filled, we can solve for the result.
    if (filledCircularVariables.length === 1) {
      const [filledVariable] = filledCircularVariables;
      // If the variable we are solving for is the one that's filled, no dependencies are needed.
      if (filledVariable === solveForVariable) {
        return [];
      }
      // Otherwise, check if we can solve for the result already
      const availableVariables = {
        ...STOICHIOMETRY_CALCULATION_AVAILABLE_VARIABLES,
        [filledVariable]: true,
      };
      const canSolve = StoichiometrySolver.canSolve(solveForVariable, availableVariables);
      if (canSolve) {
        const {usedDeps} = canSolve;
        const depsArray = OrderedStoichiometryCalculationVariables.map((variable) =>
          usedDeps.has(variable) ? (variableToCellIndexMap[variable] ?? '') : ''
        );
        return ['', toSpreadsheetBooleanValue(false), ...depsArray];
      }
    }
    // If not exactly 1 of the variables has been filled, give an error to the user.
    const columnsToFill = abbreviateList(
      [...circularVariables].map(
        (variable) =>
          COLUMN_GROUP_TO_NAME_MAP[
            findKey(
              COLUMN_GROUP_TO_STOICHIOMETRY_VARIABLE_MAP,
              (val) => val === variable
            ) as keyof typeof COLUMN_GROUP_TO_NAME_MAP
          ]
      ) ?? '',
      circularVariables.size,
      'or'
    );
    return [
      filledCircularVariables.length === 0
        ? `Must fill one of ${columnsToFill}`
        : `Must fill only one of ${columnsToFill}`,
    ];
  }

  override getTableSpecificCustomEvalArgs(
    columns: Array<StructuredTableColumn>,
    currentColumnIndex: number,
    currentRowIndex: number,
    tableMetadata: StoichiometryStructuredTableMetadata,
    toSpreadsheetIndex: (cellId: CellIndex) => string,
    structuredTable: StoichiometryStructuredTable
  ): Array<string> {
    assert(
      Boolean(structuredTable),
      'Passing full structured table is required for chemistry table custom evals'
    );
    const customEvalDependencies = this.getCustomEvalDependencies(structuredTable, {
      row: currentRowIndex,
      column: currentColumnIndex,
    });
    // If the dependency is a string, just return that and it will be converted to a TypedValue.
    // Otherwise, convert cell indexes to spreadsheet reference strings.
    const dependentSpreadsheetReferences = customEvalDependencies.map((dep) =>
      typeof dep === 'string' ? dep : `=${toSpreadsheetIndex(dep)}`
    );
    return dependentSpreadsheetReferences;
  }

  override getTableSpecificCustomEvalFn(
    column: StructuredTableColumn,
    row: StructuredTableRow<StoichiometryStructuredTableRowMetadata>,
    metadata: StoichiometryStructuredTableMetadata
  ): CustomEval<{
    tableMetadata: StoichiometryStructuredTableMetadata;
    rowMetadata: StoichiometryStructuredTableRowMetadata;
  }> | null {
    const {columnGroup} = column;
    if (!columnGroup) {
      return null;
    }
    switch (columnGroup) {
      case StructuredTableColumnGroups.CHEMISTRY_LIMITING_REACTANT:
        return limitingReactantCustomEval;
      case StructuredTableColumnGroups.CHEMISTRY_CHEMICAL_FORMULA:
        return getMolecularFormulaColumnCustomEval;
      case StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT:
        return getMolecularWeightColumnCustomEval;
      case StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT:
        return getDefaultUnitCustomEval(DEFAULT_MOL_UNIT_SYMBOL);
      case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT:
        return getDefaultUnitCustomEval(DEFAULT_MASS_UNIT_SYMBOL);
      case StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE:
      case StructuredTableColumnGroups.CHEMISTRY_MOLS:
      case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN: {
        const solveForVariable = this.getStoichiometryVariableFromColumnGroup(columnGroup);
        return makeStoichiometryCalculationCustomEval(solveForVariable);
      }
      default:
        return null;
    }
  }

  override experimental_renderAboveTableCustomComponent(
    props: StoichiometryTableCustomSlotProps
  ): React.ReactNode {
    return <StoichiometryTableEmbeddedChemicalSketcher {...props} />;
  }
}
